import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ConversationStatus } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import arrowUpLargeTokens from '@amzn/meridian-tokens/base/icon/arrow-up-large';
import filterTokens from '@amzn/meridian-tokens/base/icon/filter';
import uploadLargeTokens from '@amzn/meridian-tokens/base/icon/upload-large';
import Icon from '@amzn/meridian/icon';
import { RowWidth } from '@amzn/meridian/row/row';

import './AIChatInput.scss';

interface IAIChatInput {
    conversationStatus: ConversationStatus;
    handleSendMessage: (message: string) => Promise<void>;
    isSending: boolean;
    isDisabled: boolean;
    width?: RowWidth;
    conversationId?: string;
    showTechnicalMessages?: boolean;
    onToggleTechnicalMessages?: (value: boolean) => void;
    onAttachFile?: () => void;
}

export const AIChatInput: React.FC<IAIChatInput> = (props) => {
    const {
        conversationStatus,
        handleSendMessage,
        isSending,
        isDisabled,
        conversationId,
        showTechnicalMessages,
        onToggleTechnicalMessages,
        onAttachFile
    } = props;
    const [inputMessage, setInputMessage] = useState('');
    const [showMenu, setShowMenu] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const handleSend = useCallback(() => {
        if (!inputMessage.trim() || isSending || isDisabled) return;

        handleSendMessage(inputMessage)
            .then(() => {
                setInputMessage('');
            })
            .catch((error) => {
                console.error('Error sending message:', error);
            });
    }, [inputMessage, handleSendMessage, isSending, isDisabled]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSend();
            }
        },
        [handleSend]
    );

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
        }
    }, [inputMessage]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const placeholder =
        conversationStatus === ConversationStatus.CLOSED || conversationStatus === ConversationStatus.ERROR
            ? 'This chat is closed'
            : conversationStatus === ConversationStatus.WORKING
              ? 'AI is processing...'
              : 'Message AI...';

    const canSend = inputMessage.trim() && !isSending && !isDisabled;

    return (
        <div className="ai-chat-input">
            <div className="ai-chat-input__main">
                <textarea
                    ref={textareaRef}
                    className="ai-chat-input__textarea"
                    placeholder={placeholder}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isSending || isDisabled}
                    rows={1}
                />
                <button
                    className={`ai-chat-input__send-button ${canSend ? 'ai-chat-input__send-button--active' : ''}`}
                    onClick={handleSend}
                    disabled={!canSend}
                    aria-label="Send message"
                >
                    <Icon tokens={arrowUpLargeTokens} />
                </button>
            </div>
            {conversationId && (
                <div className="ai-chat-input__toolbar">
                    <div className="ai-chat-input__toolbar-left">
                        {onAttachFile && (
                            <div className="ai-chat-input__tooltip-wrapper">
                                <button
                                    className="ai-chat-input__icon-button"
                                    onClick={onAttachFile}
                                    aria-label="Attach file"
                                    type="button"
                                >
                                    <Icon tokens={uploadLargeTokens} />
                                </button>
                                <span className="ai-chat-input__tooltip">Upload Document</span>
                            </div>
                        )}
                        {onToggleTechnicalMessages && (
                            <div className="ai-chat-input-menu" ref={menuRef}>
                                <div className="ai-chat-input__tooltip-wrapper">
                                    <button
                                        className="ai-chat-input__icon-button"
                                        onClick={() => setShowMenu(!showMenu)}
                                        aria-label="Settings"
                                    >
                                        <Icon tokens={filterTokens} />
                                    </button>
                                    <span className="ai-chat-input__tooltip">Settings</span>
                                </div>
                                {showMenu && (
                                    <div className="ai-chat-input-menu__dropdown">
                                        <div className="ai-chat-input-menu__item">
                                            <span>Show thinking details</span>
                                            <label className="ai-chat-input-menu__toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={showTechnicalMessages}
                                                    onChange={(e) => onToggleTechnicalMessages(e.target.checked)}
                                                />
                                                <span className="ai-chat-input-menu__toggle-slider"></span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
